// A regra do Simples Nacional — LC 123/2006, art. 18. Pura, num lugar só, com
// prova própria (`app/tests/simples-nacional.mjs`).
//
// ---------------------------------------------------------------------------
// A tabela vem do BANCO, e este módulo não guarda número nenhum
// ---------------------------------------------------------------------------
// As 30 faixas (5 anexos x 6) e a repartição dos 8 tributos chegam por
// `rs_catalogo()`. Na origem elas existiam DUAS vezes — a tabela `anexos_faixas`
// no banco, que os simuladores liam, e uma cópia cravada em `AnexoConsulta.tsx`,
// que a tela de consulta mostrava. As duas concordavam; concordar é o estado
// normal de duas cópias antes da primeira divergir.
//
// ---------------------------------------------------------------------------
// O QUE A ORIGEM ERRAVA, E ESTE MÓDULO NÃO PODE VOLTAR A ERRAR
// ---------------------------------------------------------------------------
// A repartição dos tributos é por (anexo, FAIXA). O simulador da Reforma da
// origem usava uma tabela por ANEXO, ignorando a faixa — no Anexo III, 1ª faixa,
// ele dizia IRPJ 6,00% onde o oficial diz 4,00% e Cofins 13,64% onde o oficial
// diz 12,82%. Como o comparativo "Atual x Reforma" sai de PIS + Cofins e
// ICMS + ISS, o erro contaminava o número que o contador leva para o cliente.
//
// Por isso `reparticaoDe` exige a faixa, e não tem valor padrão para ela.
//
// ---------------------------------------------------------------------------
// A 6ª FAIXA NÃO É UMA FAIXA COMO AS OUTRAS
// ---------------------------------------------------------------------------
// Nela o ICMS (Anexos I e II) e o ISS (III, IV e V) saem do DAS: a empresa
// ultrapassou o sublimite estadual/municipal e recolhe esses dois em guia
// própria. O dado já diz isso — a repartição da 6ª linha traz ICMS e ISS
// zerados —, e a tela precisa DIZER, porque um tributo que aparece com 0% ao
// lado de outros com número é lido como "não paga", e o certo é "paga fora
// daqui". Ausência de linha não informa nada: é a cicatriz do "Limite com a
// prorrogação" do HUB DP.

// ---------------------------------------------------------------------------
// Os tipos
// ---------------------------------------------------------------------------

/** Os oito tributos que compõem o DAS, em percentual DO PRÓPRIO DAS. */
export interface Reparticao {
  IRPJ: number;
  CSLL: number;
  COFINS: number;
  PIS: number;
  CPP: number;
  IPI: number;
  ICMS: number;
  ISS: number;
}

export interface Faixa {
  anexo: number;
  faixa: number;
  receita_min: number;
  receita_max: number;
  aliquota_nominal: number;
  parcela_deduzir: number;
  reparticao: Reparticao;
}

/** Ordem de exibição — a mesma da tabela oficial da LC 123. */
export const TRIBUTOS: (keyof Reparticao)[] = [
  "IRPJ",
  "CSLL",
  "COFINS",
  "PIS",
  "CPP",
  "IPI",
  "ICMS",
  "ISS",
];

export const NOME_DO_TRIBUTO: Record<keyof Reparticao, string> = {
  IRPJ: "IRPJ",
  CSLL: "CSLL",
  COFINS: "Cofins",
  PIS: "PIS/Pasep",
  CPP: "CPP",
  IPI: "IPI",
  ICMS: "ICMS",
  ISS: "ISS",
};

export const NOME_DO_ANEXO: Record<number, string> = {
  1: "Anexo I — Comércio",
  2: "Anexo II — Indústria",
  3: "Anexo III — Serviços",
  4: "Anexo IV — Serviços (CPP fora do DAS)",
  5: "Anexo V — Serviços (Fator R)",
};

export const RESUMO_DO_ANEXO: Record<number, string> = {
  1: "Atividades de comércio em geral",
  2: "Atividades industriais",
  3: "Serviços tributados pela regra geral",
  4: "Serviços como vigilância, limpeza e construção civil",
  5: "Serviços intelectuais, técnicos e científicos",
};

// ---------------------------------------------------------------------------
// Os números que são da LEI, e não do banco
// ---------------------------------------------------------------------------
//
// Pela regra do corte (000108): muda por ato do Executivo ou lei anual → banco;
// percentual estrutural → constante. Estes três são estruturais da LC 123 e
// mudariam só com reforma da própria lei — que é deploy de todo modo.

/** LC 123/2006, art. 3º, II — o teto do Simples Nacional. */
export const TETO_ANUAL = 4_800_000;

/** LC 123/2006, art. 3º, I — até aqui a empresa é ME; acima, EPP. */
export const LIMITE_ME = 360_000;

/** LC 123/2006, art. 13-A e 19 — acima disto, ICMS e ISS saem do DAS. */
export const SUBLIMITE = 3_600_000;

/** LC 123/2006, art. 18, §5º-J e §5º-M — o corte do Fator R. */
export const CORTE_FATOR_R = 0.28;

// ---------------------------------------------------------------------------
// Dinheiro em centavos
// ---------------------------------------------------------------------------
//
// Vem do HUB DP, e não é reescrito aqui. A primeira versão deste módulo tinha
// `Math.round((v + EPSILON) * 100) / 100`, que é EXATAMENTE a forma que o
// comentário de `folhaDp.centavos` registra ter errado um centavo no INSS de
// quem ganha um salário mínimo: o resíduo binário nasce na multiplicação, e
// somar EPSILON antes não o alcança. Arredondamento de dinheiro é um problema
// só, e ele já foi pago uma vez nesta casa.
export { centavos } from "./folhaDp";
import { centavos } from "./folhaDp";

// ---------------------------------------------------------------------------
// A faixa
// ---------------------------------------------------------------------------

export type MotivoSemFaixa = "sem-receita" | "acima-do-teto" | "anexo-desconhecido";

export interface Enquadramento {
  faixa: Faixa | null;
  motivo: MotivoSemFaixa | null;
}

/**
 * Acha a faixa de um anexo para uma RBT12.
 *
 * Devolve o MOTIVO quando não acha, em vez de nulo mudo. A origem mostrava
 * "Nenhuma faixa do Anexo N contempla a receita de R$ X" para os três casos —
 * inclusive para quem passou dos R$ 4,8 milhões, que é uma informação
 * completamente diferente ("sua empresa não cabe mais no Simples") e a mais
 * importante que a tela tinha para dar.
 */
export function enquadrar(faixas: Faixa[], anexo: number, rbt12: number): Enquadramento {
  if (!(rbt12 > 0)) return { faixa: null, motivo: "sem-receita" };

  const doAnexo = faixas.filter((f) => f.anexo === anexo);
  if (doAnexo.length === 0) return { faixa: null, motivo: "anexo-desconhecido" };

  const achada = doAnexo.find((f) => rbt12 >= f.receita_min && rbt12 <= f.receita_max);
  if (achada) return { faixa: achada, motivo: null };

  // Passou do teto do anexo. As faixas cobrem de 0 a 4.800.000 sem vão (o ETL
  // confere isso na carga), então o único jeito de não achar é estar acima.
  return { faixa: null, motivo: "acima-do-teto" };
}

// ---------------------------------------------------------------------------
// A alíquota efetiva
// ---------------------------------------------------------------------------

export interface ResultadoAliquota {
  faixa: Faixa;
  rbt12: number;
  /** Em PERCENTUAL (6,4 e não 0,064) — é como o contador fala e como a tela mostra. */
  aliquotaEfetiva: number;
  receitaDoMes: number;
  das: number;
  /** O valor de cada tributo dentro do DAS do mês. */
  composicao: Record<keyof Reparticao, number>;
  /** Na 6ª faixa, ICMS ou ISS saem do DAS — e a tela precisa DIZER. */
  tributosForaDoDas: (keyof Reparticao)[];
}

/**
 * (RBT12 × alíquota nominal − parcela a deduzir) ÷ RBT12.
 *
 * O denominador é a RBT12, e não a receita do mês: a alíquota efetiva é uma
 * propriedade dos últimos 12 meses. A receita do mês só entra depois, para
 * virar o valor do DAS.
 */
export function calcularAliquota(
  faixas: Faixa[],
  anexo: number,
  rbt12: number,
  receitaDoMes: number,
): ResultadoAliquota | { erro: MotivoSemFaixa } {
  const { faixa, motivo } = enquadrar(faixas, anexo, rbt12);
  if (!faixa) return { erro: motivo ?? "anexo-desconhecido" };

  const bruto = (rbt12 * (faixa.aliquota_nominal / 100) - faixa.parcela_deduzir) / rbt12;
  // Guarda de sanidade: alíquota negativa sairia de uma parcela a deduzir maior
  // que o tributo da faixa, o que é dado sujo. Zero é o piso, e a tela nunca
  // mostra "-2%" com cara de desconto.
  const efetiva = Math.max(0, bruto);
  const mes = Math.max(0, receitaDoMes);
  const das = centavos(mes * efetiva);

  const composicao = {} as Record<keyof Reparticao, number>;
  for (const t of TRIBUTOS) composicao[t] = centavos(das * (faixa.reparticao[t] / 100));

  // Na 6ª faixa, o tributo que a repartição zera é o que saiu do DAS. Ele só
  // conta como "fora" se o anexo o cobrar nas outras faixas — senão o IPI
  // apareceria como "fora do DAS" no Anexo III, onde ele simplesmente não existe.
  const tributosForaDoDas: (keyof Reparticao)[] = [];
  if (faixa.faixa === 6) {
    const outras = faixas.filter((f) => f.anexo === anexo && f.faixa < 6);
    for (const t of TRIBUTOS) {
      const cobraNasOutras = outras.some((f) => f.reparticao[t] > 0);
      if (cobraNasOutras && faixa.reparticao[t] === 0) tributosForaDoDas.push(t);
    }
  }

  return {
    faixa,
    rbt12,
    aliquotaEfetiva: Math.round(efetiva * 1e6) / 1e4,
    receitaDoMes: mes,
    das,
    composicao,
    tributosForaDoDas,
  };
}

// ---------------------------------------------------------------------------
// O Fator R
// ---------------------------------------------------------------------------

export interface ResultadoFatorR {
  /** Em PERCENTUAL. */
  fatorR: number;
  /** 3 quando o Fator R alcança 28%; 5 quando não. */
  anexo: 3 | 5;
  alcancou: boolean;
  folha12m: number;
  rbt12: number;
  /**
   * Quanto faltou de FOLHA para chegar ao Anexo III, em reais. Nulo quando já
   * alcançou.
   *
   * A origem dizia "considere aumentar a proporção da folha de pagamento" sem
   * número — e o número é o que transforma o conselho em decisão: com R$ 500 mil
   * de receita e R$ 100 mil de folha, faltam R$ 40.000, não "um pouco mais".
   */
  faltaDeFolha: number | null;
}

/**
 * Fator R = folha de 12 meses ÷ receita bruta de 12 meses.
 *
 * O corte é `>= 28%` (LC 123/2006, art. 18, §5º-M). **Igual a 28% vai para o
 * Anexo III** — o `>=` é a lei, e trocá-lo por `>` empurra para o anexo mais
 * caro exatamente quem está no limite.
 */
export function calcularFatorR(rbt12: number, folha12m: number): ResultadoFatorR | { erro: "sem-receita" } {
  if (!(rbt12 > 0)) return { erro: "sem-receita" };

  const folha = Math.max(0, folha12m);
  const razao = folha / rbt12;
  const alcancou = razao >= CORTE_FATOR_R;

  return {
    fatorR: Math.round(razao * 1e6) / 1e4,
    anexo: alcancou ? 3 : 5,
    alcancou,
    folha12m: folha,
    rbt12,
    faltaDeFolha: alcancou ? null : centavos(rbt12 * CORTE_FATOR_R - folha),
  };
}

// ---------------------------------------------------------------------------
// A repartição
// ---------------------------------------------------------------------------

/**
 * A repartição de um par (anexo, faixa).
 *
 * Não existe valor padrão para a faixa, de propósito: foi omitir a faixa que
 * fez o simulador da Reforma da origem mostrar IRPJ 6,00% onde o oficial diz
 * 4,00%.
 */
export function reparticaoDe(faixas: Faixa[], anexo: number, faixa: number): Reparticao | null {
  return faixas.find((f) => f.anexo === anexo && f.faixa === faixa)?.reparticao ?? null;
}

// ---------------------------------------------------------------------------
// O enquadramento como ME ou EPP
// ---------------------------------------------------------------------------

export type Porte = "ME" | "EPP" | "acima-do-teto";

export function porteDe(receitaAnual: number): Porte {
  if (receitaAnual > TETO_ANUAL) return "acima-do-teto";
  return receitaAnual <= LIMITE_ME ? "ME" : "EPP";
}

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

export const emReais = (v: number): string =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const emPercentual = (v: number, casas = 2): string =>
  `${v.toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;

/** O ordinal da faixa, como a tabela oficial escreve: "1ª", "6ª". */
export const ordinalDaFaixa = (n: number): string => `${n}ª`;

/** O numeral romano do anexo, como o contador fala: "Anexo III". */
export const ROMANO: Record<number, string> = { 1: "I", 2: "II", 3: "III", 4: "IV", 5: "V" };
